import "./App.css";

import { Sidebar } from "./sidebar";
import { MobileBottomNavigation } from "./components/mobile-bottom-navigation";
import {
  Container,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
} from "@mui/material";

import { Route, Routes } from "react-router-dom";
import {
  HomeRoute,
  ProductsRoute,
  ViewProductRoute,
  PurchaseOrdersRoute,
  QuotesRoute,
  EditQuoteRoute,
  ViewQuoteRoute,
  BillsRoute,
  SuppliersRoute,
  EditPurchaseOrderRoute,
  ViewPurchaseOrderRoute,
  AdjustStockRoute,
  EditProductRoute,
  LocationsRoute,
  StocktakeRoute,
  CustomSalesRoute,
  StockValueRoute,
  AuditLogRoute,
} from "./routes";

import ProductsView from "./views/products";
import ViewProductView from "./views/view-product";
import PurchaseOrdersView from "./views/purchase-orders";
import EditPurchaseOrderView from "./views/edit-purchaseorder";
import QuotesView from "./views/quotes";
import EditQuoteView from "./views/edit-quote";
import ViewQuoteView from "./views/view-quote";
import BillsView from "./views/bills";
import SuppliersView from "./views/suppliers";
import GlobalMessageProvider from "./context/globalMessage.provider";
import ErrorBoundary from "./components/error-boundary.component";
import GlobalMessageDisplay from "./components/global.message.display";
import ViewPurchaseOrderView from "./views/view-purchaseorder";
import CustomSalesView from "./views/custom-sales";
import { useAppAuth } from "./hooks/useAppAuth";
import { AdjustStockView } from "./views/adjust-stock";
import EditProductView from "./views/edit-product";
import { useEffect } from "react";
import LocationsView from "./views/locations";
import StocktakeView from "./views/stocktake";
import StockValueView from "./views/stock-value";
import AuditLogsView from "./views/audit-logs";
import { AuthTokenManager } from "./components/AuthTokenManager";
import { UpdateProvider } from "./context/update.provider";
import { UpdateModal } from "./components/modal.update-client";

const theme = createTheme({
  typography: {
    h1: {
      fontFamily: "Montserrat",
      fontSize: "4em",
    },
    h2: {
      fontFamily: "Montserrat",
      fontSize: "2em",
    },
    h3: {
      fontFamily: "Montserrat",
    },
    h4: {
      fontFamily: "Montserrat",
    },
    h5: {
      fontWeight: "bold",
    },
    h6: {
      fontWeight: "bold",
    },
  },
  palette: {
    primary: {
      main: "#BA2224",
    },
    secondary: {
      main: "#DCDCDC",
    },
  },
});

function App() {
  const { isAuthenticated, isLoading, user, loginWithRedirect, logout } =
    useAppAuth();

  // Show loading state while Auth0 is initializing
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  // Redirect to login if not authenticated
  // Only redirect if we're not loading AND not already processing a callback
  if (!isAuthenticated && !isLoading && !window.location.search.includes('code=')) {
    // Clear local storage while we're here
    localStorage.clear();
    loginWithRedirect();
    return null;
  }

  // If we're processing an Auth0 callback, show loading
  if (window.location.search.includes('code=') && !isAuthenticated) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Processing login...
      </div>
    );
  }

  // Check if user has amajormusic.co.uk email domain
  // This is now also enforced by the backend, but we can show a friendly message
  const userEmail = user?.email;
  const isValidDomain = userEmail && userEmail.toLowerCase().endsWith('@amajormusic.co.uk');
  
  if (!isValidDomain) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2>Access Restricted</h2>
        <p>This application is only available to users with an @amajormusic.co.uk email address.</p>
        <p>You are currently signed in as: {userEmail}</p>
        <button 
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          style={{ marginTop: '20px', padding: '10px 20px' }}
        >
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <AuthTokenManager>
      <UpdateProvider>
        <div className="App">
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <GlobalMessageProvider>
              <Container maxWidth={false}>
                <Box>
                  <Sidebar />
                </Box>
                <Box sx={{marginLeft: { xs: 0, sm: "240px" }, paddingBottom: { xs: "80px", sm: 0 }}}>
                  <GlobalMessageDisplay />
                  <ErrorBoundary>
                  <Routes>
                    <Route path={HomeRoute} element={<ProductsView />} />
                    <Route path={ProductsRoute} element={<ProductsView />} />
                    <Route
                      path={ViewProductRoute}
                      element={<ViewProductView />}
                    />
                    <Route
                      path={EditProductRoute}
                      element={<EditProductView />}
                    />
                    <Route
                      path={PurchaseOrdersRoute}
                      element={<PurchaseOrdersView />}
                    />
                    <Route
                      path={ViewPurchaseOrderRoute}
                      element={<ViewPurchaseOrderView />}
                    />
                    <Route
                      path={EditPurchaseOrderRoute}
                      element={<EditPurchaseOrderView />}
                    />
                    <Route
                      path={QuotesRoute}
                      element={<QuotesView />}
                    />
                    <Route
                      path={ViewQuoteRoute}
                      element={<ViewQuoteView />}
                    />
                    <Route
                      path={EditQuoteRoute}
                      element={<EditQuoteView />}
                    />
                    <Route path={BillsRoute} element={<BillsView />} />
                    <Route
                      path={AdjustStockRoute}
                      element={<AdjustStockView />}
                    />
                    <Route path={SuppliersRoute} element={<SuppliersView />} />
                    <Route path={LocationsRoute} element={<LocationsView />} />
                    <Route path={StocktakeRoute} element={<StocktakeView />} />
                    <Route path={CustomSalesRoute} element={<CustomSalesView />} />
                    <Route path={StockValueRoute} element={<StockValueView />} />
                    <Route path={AuditLogRoute} element={<AuditLogsView />} />
                  </Routes>
                  </ErrorBoundary>
                </Box>
              </Container>
              <MobileBottomNavigation />
              <UpdateModal />
            </GlobalMessageProvider>
          </ThemeProvider>
        </div>
      </UpdateProvider>
    </AuthTokenManager>
  );
}

export default App;
